import { useState } from "react";

import { ArrowUpDown, Copy, Ellipsis, Loader2 } from "lucide-react";

import type { ColumnDef } from "@tanstack/react-table";

import { parseAsString, useQueryState } from "nuqs";

import { Button } from "@/shared/ui/button";
import { formatDate, numberWithSpaces } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import { useToast } from "@/shared/hooks/useToast";
import { useURLParamsData } from "@/shared/hooks/useURLParamsData.ts";
import { SortButton } from "@/features/SortButton/SortButton";
import { ProfitHoverCard } from "@/features/WorkerLeadProfitHoverCard";
import { QRGeneratorModal } from "./QRGeneratorModal";

import { Modal } from "@/shared/ui/Modal";
import { Label } from "@/shared/ui/label";

import { LinksDescriptionModal } from "./LinksDescriptionModal";
import { DepositsHoverCard } from "./DepositsHoverCard";
import { type LinkColumnsConfig } from "../model/link-columns";
import { EditLinkModal } from "./EditLinkModal";
import { useGetBotById } from "@/entities/bot/api/api";
import {
  LeadLinkStatus,
  useArchiveLink,
  useRestoreLink,
} from "@/entities/lead-link";

export const LinksColumns: ColumnDef<LinkColumnsConfig>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: "Название",
    cell: ({ row }) => <span className="text-nowrap">{row.original.name}</span>,
  },
  {
    id: "bot-info",
    header: "Бот",
    cell: function Header({ row }) {
      const [open, setOpen] = useState(false);

      return (
        row.original.bot && (
          <BotInformationModal
            open={open}
            setOpen={setOpen}
            id={row.original.bot._id}
          />
        )
      );
    },
  },
  {
    id: "bot",
    header: () => <span className="text-nowrap">Бот ссылка</span>,
    cell: function Cell({ row }) {
      const { data: bot } = useGetBotById(row.original.bot?._id);
      return (
        <a
          href={`/bots/?search=${encodeURIComponent(bot?.data.telegramNickname ?? "")}`}
        >
          {bot?.data.telegramNickname}
        </a>
      );
    },
  },
  {
    id: "description",
    accessorKey: "description",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Описание
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const description = row.original.description;
      return (
        <DataTableCenteredCell>
          <LinksDescriptionModal description={description} />
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "leadsCount",
    accessorKey: "leadsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort={"leadsCount"} title="Лидов" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.leadsCount}</DataTableCenteredCell>
    ),
  },
  {
    id: "costs",
    accessorKey: "costs",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Траты
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const costs = row.original.costs;
      return <DataTableCenteredCell>{costs}</DataTableCenteredCell>;
    },
  },
  {
    id: "totalProfit",
    accessorKey: "totalProfit",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort={"totalProfit"} title="Заработок" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const totalProfit = row.original.totalProfit;
      const linkId = row.original._id;
      const formattedEarnings =
        typeof totalProfit === "number" ? numberWithSpaces(totalProfit) : "N/A";

      return (
        <DataTableCenteredCell>
          <ProfitHoverCard
            formattedEarnings={formattedEarnings}
            linkId={linkId}
          />
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "depositsCount",
    accessorKey: "depositsCount",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort={"depositsCount"} title="Кол-во Депов" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const { depositsCount, redepositsCount } = row.original;

      return (
        <DataTableCenteredCell>
          <DepositsHoverCard
            uniqueDeposits={depositsCount - redepositsCount}
            uniqueMultiplier={redepositsCount}
          />
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort={"createdAt"} title="Создана" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.createdAt ? formatDate(row.original.createdAt) : "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "CR",
    accessorKey: "CR",
    header: () => (
      <DataTableCenteredCell className="text-thColor">CR</DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.CR}</DataTableCenteredCell>
    ),
  },
  {
    id: "AD",
    accessorKey: "AD",
    header: () => (
      <DataTableCenteredCell className="text-thColor">AD</DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.AD}</DataTableCenteredCell>
    ),
  },
  {
    id: "AP",
    accessorKey: "AP",
    header: () => (
      <DataTableCenteredCell className="text-thColor">AP</DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.AP}</DataTableCenteredCell>
    ),
  },
  {
    id: "CPC",
    accessorKey: "CPC",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        CPC
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.CPC}</DataTableCenteredCell>
    ),
  },
  {
    id: "CPL",
    accessorKey: "CPL",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        CPL
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.CPL}</DataTableCenteredCell>
    ),
  },
  {
    id: "CPS",
    accessorKey: "CPS",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        CPS
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.CPS}</DataTableCenteredCell>
    ),
  },
  {
    id: "EPC",
    accessorKey: "EPC",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        EPC
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.EPC}</DataTableCenteredCell>
    ),
  },
  {
    id: "EPL",
    accessorKey: "EPL",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        EPL
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.EPL}</DataTableCenteredCell>
    ),
  },
  {
    id: "EPS",
    accessorKey: "EPS",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        EPS
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>{row.original.EPS}</DataTableCenteredCell>
    ),
  },
  {
    id: "type",
    accessorKey: "type",
    header: function Header() {
      const { setType } = useURLParamsData();

      return (
        <DropdownMenu>
          <DataTableCenteredCell className="flex flex-row">
            <DropdownMenuTrigger asChild>
              <Button variant="ghost">
                <p className="text-thColor">Тип</p>
                <ArrowUpDown className="ml-2 h-4 w-4 stroke-thColor" />
              </Button>
            </DropdownMenuTrigger>
          </DataTableCenteredCell>
          <DropdownMenuContent>
            <DropdownMenuItem
              className="overflow-y-auto"
              onClick={() => {
                setType("");
              }}
            >
              Все
            </DropdownMenuItem>
            <DropdownMenuItem
              className="overflow-y-auto"
              onClick={() => {
                setType("personal");
              }}
            >
              Личная
            </DropdownMenuItem>
            <DropdownMenuItem
              className="overflow-y-auto"
              onClick={() => {
                setType("pair");
              }}
            >
              Парная
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.type === "personal" ? "Личная" : "Парная"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "url",
    accessorKey: "url",
    header: "Ссылка",
    cell: function Cell({ row }) {
      const { toast } = useToast();
      const { mutateAsync: archiveLink, isPending: isArchiving } =
        useArchiveLink();
      const { mutateAsync: restoreLink, isPending: isRestoring } =
        useRestoreLink();
      const [openQRGenerator, setOpenQRGenerator] = useState(false);
      const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
      const [openEditModal, setOpenEditModal] = useState(false);
      const [target] = useQueryState("target", parseAsString);

      const handleCopy = () => {
        navigator.clipboard.writeText(row.original.url);
        toast({
          title: "Успешно!",
          description: "Ссылка скопирована в буфер обмена",
        });
      };

      const handleArchiveLink = async () => {
        await archiveLink({ id: row.original._id });
      };

      const handleRestoreLink = async () => {
        await restoreLink({ id: row.original._id });
      };

      const link = {
        id: row.original._id,
        name: row.original.name,
        description: row.original.description,
        costs: row.original.costs,
      };

      return (
        <DataTableCenteredCell>
          <QRGeneratorModal
            open={openQRGenerator}
            onClose={() => setOpenQRGenerator(false)}
            linkName={row.original.name}
            link={row.original.url}
            target={row.original.target}
          />
          <EditLinkModal
            link={link}
            disabled={target === "constructor-bot"}
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
          />
          <Modal
            open={openConfirmationModal}
            onOpenChange={setOpenConfirmationModal}
          >
            <Modal.Content>
              <Modal.Title>Подтверждение</Modal.Title>
              <Label>Вы уверены, что хотите удалить ссылку?</Label>
              <Button disabled={isArchiving} onClick={handleArchiveLink}>
                Подтвердить
              </Button>

              <Modal.Close />
            </Modal.Content>
          </Modal>
          <div className="flex">
            <Button variant="ghost" onClick={handleCopy}>
              <Copy className="my-auto self-end stroke-previousText" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Ellipsis className="my-auto self-end stroke-previousText" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleCopy}>
                  Копировать
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setOpenQRGenerator(true);
                  }}
                >
                  Сгенерировать QR-код
                </DropdownMenuItem>
                {row.original.status === LeadLinkStatus.ACTIVE ? (
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setOpenConfirmationModal(true);
                    }}
                    className="bg-red-500 text-center text-neutral-50 shadow-sm hover:bg-red-500/90"
                  >
                    Архивировать
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={handleRestoreLink}
                    className="bg-red-500 text-center text-neutral-50 shadow-sm hover:bg-red-500/90"
                    disabled={isRestoring}
                  >
                    Восстановить
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={row.original.status === LeadLinkStatus.ARCHIVE}
                  onSelect={(e) => {
                    e.preventDefault();
                    setOpenEditModal(true);
                  }}
                >
                  Редактировать
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DataTableCenteredCell>
      );
    },
  },
];

const BotInformationModal = ({
  id,
  open,
  setOpen,
}: {
  id: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { data, isPending } = useGetBotById(id, open);

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <Modal.Trigger>Раскрыть</Modal.Trigger>
      <Modal.Content>
        <Modal.Title>Информация о боте</Modal.Title>
        {isPending && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        )}
        {!isPending && data && (
          <div className="space-y-4">
            <div className="grid gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                <Label className="text-sm font-medium text-gray-700">
                  Описание
                </Label>
                <p className="mt-1 text-sm leading-relaxed text-gray-900">
                  {data.data.description || "Описание не указано"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                <Label className="text-sm font-medium text-gray-700">
                  Приветственное сообщение
                </Label>
                <p className="mt-1 text-sm leading-relaxed text-gray-900">
                  {data.data.greetingText || "Сообщение не указано"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                <Label className="text-sm font-medium text-gray-700">
                  Изображение
                </Label>
                {data.data.greetingImageUrl ? (
                  <div className="mt-2">
                    <a
                      href={data.data.greetingImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      Открыть изображение
                    </a>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    Изображение не загружено
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                <Label className="text-sm font-medium text-gray-700">
                  Текст кнопки
                </Label>
                <p className="mt-1 text-sm leading-relaxed text-gray-900">
                  {data.data.greetingButtonText || "Текст кнопки не указан"}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};
